import React from 'react';
import { Box, Container, Typography, Grid, Paper, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import LanguageIcon from '@mui/icons-material/Language';

const About = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const paperVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.02,
      boxShadow: "0 8px 30px rgba(34, 225, 101, 0.1)",
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const iconVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "backOut"
      }
    },
    hover: {
      scale: 1.2,
      rotate: 5,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const languageItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
    hover: {
      x: 5,
      background: "linear-gradient(90deg, rgba(34, 225, 101, 0.15) 0%, rgba(34, 225, 101, 0.1) 100%)",
      transition: {
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  return (
    <Box
      id="about"
      sx={{
        py: 8,
        background: 'linear-gradient(180deg, #050810 0%, #0a1428 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div variants={itemVariants}>
            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 6,
                fontWeight: 700,
                background: 'linear-gradient(45deg, #ffffff 30%, #22e165 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('about.title')}
            </Typography>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                mb: 8,
                color: 'rgba(255, 255, 255, 0.8)',
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.8
              }}
            >
              {t('about.description')}
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {/* Education */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    component={motion.div}
                    variants={paperVariants}
                    whileHover="hover"
                    sx={{
                      p: 4,
                      height: '100%',
                      background: 'rgba(34, 225, 101, 0.05)',
                      border: '1px solid rgba(34, 225, 101, 0.1)',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <motion.div variants={iconVariants} whileHover="hover">
                        <SchoolIcon sx={{ color: theme.palette.primary.main, mr: 2, fontSize: 32 }} />
                      </motion.div>
                      <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                        {t('about.education.title')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                      {t('about.education.degree')}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                      {t('about.education.university')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                      {t('about.education.period')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {t('about.education.thesis')}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Languages */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    component={motion.div}
                    variants={paperVariants}
                    whileHover="hover"
                    sx={{
                      p: 2.5,
                      mb: 2,
                      background: 'rgba(34, 225, 101, 0.05)',
                      border: '1px solid rgba(34, 225, 101, 0.1)',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <motion.div variants={iconVariants} whileHover="hover">
                        <LanguageIcon sx={{ color: theme.palette.primary.main, mr: 1.5, fontSize: 24 }} />
                      </motion.div>
                      <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                        {t('about.languages.title')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <motion.div
                        variants={languageItemVariants}
                        whileHover="hover"
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          background: 'linear-gradient(90deg, rgba(34, 225, 101, 0.1) 0%, rgba(34, 225, 101, 0.05) 100%)',
                          cursor: 'pointer'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: 500, minWidth: '70px' }}>
                            Español
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {t('about.languages.spanish')}
                          </Typography>
                        </Box>
                      </motion.div>
                      <motion.div
                        variants={languageItemVariants}
                        whileHover="hover"
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          background: 'linear-gradient(90deg, rgba(34, 225, 101, 0.1) 0%, rgba(34, 225, 101, 0.05) 100%)',
                          cursor: 'pointer'
                        }}>
                          <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: 500, minWidth: '70px' }}>
                            English
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                            {t('about.languages.english')}
                          </Typography>
                        </Box>
                      </motion.div>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            {/* Experience */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                component={motion.div}
                variants={paperVariants}
                whileHover="hover"
                sx={{
                  p: 4,
                  height: '100%',
                  background: 'rgba(34, 225, 101, 0.05)',
                  border: '1px solid rgba(34, 225, 101, 0.1)',
                  borderRadius: 4,
                  cursor: 'pointer'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <motion.div variants={iconVariants} whileHover="hover">
                    <WorkIcon sx={{ color: theme.palette.primary.main, mr: 2, fontSize: 32 }} />
                  </motion.div>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                    {t('about.experience.title')}
                  </Typography>
                </Box>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                    {t('about.experience.current.title')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                    {t('about.experience.current.company')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    {t('about.experience.current.period')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('about.experience.current.description')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                    {t('about.experience.previous.title')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: theme.palette.primary.main, mb: 1 }}>
                    {t('about.experience.previous.company')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                    {t('about.experience.previous.period')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    {t('about.experience.previous.description')}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;